/**
 * Tab strip with split groups, Chrome-style tab groups, pinned icon tabs, multi-select (⌘/Ctrl-click), drag and drop,
 * overflow scrolling and tab search (featureFlags.tabs). A split view is one joined tab ("# general | Thread"); each
 * side has its own ✕ (and middle click / menu "Close …"), "Move to new tab", and swaps by menu, ⌘⌥\ or dragging it
 * onto the other side.
 * Below 820 px the strip collapses into a tab switcher button that opens the tab list with group sections.
 * Keyboard: ⌘⇧T reopens the last closed tab (Alt+Shift+T where the browser keeps it), Alt+Shift+A searches tabs (⌘⇧A
 * is Slack's All unreads);
 * in the strip only the entry on screen is a Tab stop, ←/→/Home/End move between entries and group chips.
 */
import type { PageNode } from "@bundle/contracts";
import { Shell, type DocsCache, type ShellState, type ShellViewModel, type TabGroup, type WorkspaceCache } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { Icon, IconButton, TextField, useT } from "@bundle/ui";
import { ContextMenu, Popover } from "radix-ui";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { isDesktop, openInNewWindow } from "../../platform/desktop.ts";
import { WEB_ORIGIN } from "../../platform/env.ts";
import { tabInfo, type TabInfo } from "../tabInfo.ts";
import { COLOR_KEYS, groupLabel, TabIcon } from "./parts.tsx";
import { applyDrop, stripItems, type DragSource, type StripItem } from "./strip.ts";
import { StripEnd, TabDndProvider, useStripItemDnd, useTabDrag } from "./TabDnd.tsx";
import { TabSearch } from "./TabSearch.tsx";
import "./tabs.css";

type Props = {
  shell: ShellViewModel;
  state: ShellState;
  cache: WorkspaceCache | undefined;
  docs?: DocsCache;
  knownPages?: Record<string, PageNode>;
};

type Info = (tabId: string) => TabInfo;

function useInfo(p: Pick<Props, "state" | "cache" | "docs" | "knownPages">): Info {
  const t = useT();
  return useCallback(
    (tabId: string) => {
      const tab = Shell.findTab(p.state, tabId);
      return tabInfo(tab?.url ?? "", p.cache, t, p.docs, p.knownPages);
    },
    [p.state, p.cache, p.docs, p.knownPages, t],
  );
}

const NARROW = "(max-width: 819px)";

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** Wraps the title bar and the panes: tab drag and drop (strip, group chips, pane edges). */
export function TabsDnd(p: Props & { children: ReactNode }) {
  const info = useInfo(p);
  const ghost = (source: DragSource) => {
    // One segment of a split tab drags alone (to swap sides).
    const ids = source.kind === "unit" ? (source.seg === undefined ? source.unit.tabIds : [source.seg]) : [];
    return source.kind === "group" ? (
      <div className="tab-group-chip tab-ghost" data-group-color={source.group.color}>
        <span className="tab-group-name">{source.group.name}</span>
      </div>
    ) : (
      <div className={["tab", "tab-ghost", ids.length > 1 && "tab-split"].filter(Boolean).join(" ")}>
        {ids.map((id, i) => (
          <Fragment key={id}>
            {i > 0 ? <span className="tab-split-divider" /> : null}
            <span className="tab-seg">
              <TabIcon info={info(id)} />
              <span className="tab-title">{info(id).title}</span>
            </span>
          </Fragment>
        ))}
      </div>
    );
  };
  return (
    <TabDndProvider shell={p.shell} ghost={ghost}>
      {p.children}
    </TabDndProvider>
  );
}

export function TabBar(p: Props) {
  const t = useT();
  const { shell, state } = p;
  const info = useInfo(p);
  const narrow = useNarrow();
  const [search, setSearch] = useState(false);
  const [picked, setSelected] = useState<string[]>([]);
  // ⌘/Ctrl-click selection, without tabs that have closed since.
  const selected = useMemo(() => picked.filter((id) => Shell.findTab(state, id) !== undefined), [picked, state]);
  const [editing, setEditing] = useState<string | undefined>(undefined);
  const items = useMemo(() => stripItems(state), [state]);

  const toggleSearch = useCallback(() => setSearch((o) => !o), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selected.length > 0) setSelected([]);
      if (!e.shiftKey || !(e.metaKey || e.ctrlKey || e.altKey)) return;
      if (e.code === "KeyT") {
        e.preventDefault();
        e.stopPropagation();
        shell.reopenClosed();
      } else if (e.code === "KeyA" && e.altKey && !e.metaKey && !e.ctrlKey) {
        // ⌥⇧A: ⌘⇧A is Slack's All unreads (useSlackShortcuts).
        e.preventDefault();
        e.stopPropagation();
        toggleSearch();
      }
    };
    // Capture: before the shell's own shortcuts (⌘T would otherwise also see ⌘⇧T).
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [shell, toggleSearch, selected.length]);

  const searchDialog = <TabSearch open={search} onOpenChange={setSearch} state={state} shell={shell} info={info} switcher={narrow} />;

  if (narrow) {
    const active = info(state.activeTabId);
    return (
      <div className="tabbar tabbar-narrow">
        <button type="button" className="tab-switcher-button" onClick={() => setSearch(true)} data-testid="tab-switcher" aria-label={t("shell.tabs.open")}>
          <TabIcon info={active} />
          <span className="tab-title">{active.title}</span>
          <span className="tab-switcher-count">{state.tabs.length}</span>
        </button>
        {searchDialog}
      </div>
    );
  }

  return (
    <div className="tabbar">
      <Strip p={p} items={items} info={info} selected={selected} setSelected={setSelected} editing={editing} setEditing={setEditing} />
      <IconButton icon="chevronDown" accessibilityLabel={t("shell.tabs.search")} onPress={() => setSearch(true)} testID="tab-search" size="sm" />
      {searchDialog}
    </div>
  );
}

/**
 * Keyboard reordering (Chrome: Ctrl+Shift+PageUp/PageDown; also ⌥⇧←/→): moves a tab entry or a group chip one place,
 * with the same rules as dropping it before / after its neighbour, and keeps focus on it.
 */
function moveByKey(e: ReactKeyboardEvent, s: StripProps, item: StripItem): boolean {
  const left = (e.ctrlKey && e.shiftKey && e.key === "PageUp") || (e.altKey && e.shiftKey && e.key === "ArrowLeft");
  const right = (e.ctrlKey && e.shiftKey && e.key === "PageDown") || (e.altKey && e.shiftKey && e.key === "ArrowRight");
  if (!left && !right) return false;
  e.preventDefault();
  const items = s.items;
  const own = item.kind === "group" ? new Set([item.key, ...items.filter((x) => x.kind === "unit" && x.group?.id === item.group.id).map((x) => x.key)]) : new Set([item.key]);
  const i = items.findIndex((x) => x.key === item.key);
  let j = i;
  do j += left ? -1 : 1;
  while (j >= 0 && j < items.length && own.has(items[j]!.key));
  const neighbour = items[j];
  if (neighbour === undefined) return true;
  const source: DragSource = item.kind === "unit" ? { kind: "unit", key: item.key, unit: item.unit } : { kind: "group", key: item.key, group: item.group };
  applyDrop(s.p.shell, s.p.state, items, source, { kind: left ? "before" : "after", key: neighbour.key });
  requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-strip-key="${CSS.escape(item.key)}"]`)?.focus());
  return true;
}

type StripProps = {
  p: Props;
  items: StripItem[];
  info: Info;
  selected: string[];
  setSelected: (f: (s: string[]) => string[]) => void;
  editing: string | undefined;
  setEditing: (id: string | undefined) => void;
};

function Strip(s: StripProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (el === null) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (el === null) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);
  useEffect(measure, [measure, s.items]);

  // Keep the entry on screen visible when it changes.
  const shownKey = s.items.find((x) => x.kind === "unit" && x.unit.shown)?.key;
  useEffect(() => {
    if (shownKey === undefined) return;
    scroller.current?.querySelector(`[data-unit="${CSS.escape(shownKey)}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [shownKey]);

  return (
    <div
      ref={scroller}
      className={["tabs", "tabs-grouped", fade.left && "tabs-fade-left", fade.right && "tabs-fade-right"].filter(Boolean).join(" ")}
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={(e) => {
        // Roving focus (WAI-ARIA tabs): only the entry on screen is a Tab stop; arrows / Home / End move between entries
        // and group chips, Enter or Space selects.
        const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
        if (!keys.includes(e.key) || e.metaKey || e.ctrlKey || e.altKey) return;
        const el = scroller.current;
        const at = (e.target as Element).closest("[data-strip-key]");
        if (el === null || at === null) return;
        const all = Array.from(el.querySelectorAll<HTMLElement>("[data-strip-key]"));
        const i = all.indexOf(at as HTMLElement);
        const next = e.key === "Home" ? 0 : e.key === "End" ? all.length - 1 : i + (e.key === "ArrowLeft" ? -1 : 1);
        const target = all[Math.max(0, Math.min(all.length - 1, next))];
        if (target === undefined) return;
        e.preventDefault();
        target.focus();
      }}
      onScroll={measure}
      onWheel={(e) => {
        // A mouse wheel scrolls the strip sideways.
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && scroller.current !== null) scroller.current.scrollLeft += e.deltaY;
      }}
    >
      {s.items.map((item) =>
        item.kind === "group" ? (
          <GroupChip key={item.key} item={item} s={s} />
        ) : (
          <UnitTab key={item.key} item={item} s={s} />
        ),
      )}
      <StripEnd />
    </div>
  );
}

function MenuItem({ icon, label, onSelect, destructive, testID }: { icon?: IconName; label: string; onSelect: () => void; destructive?: boolean; testID: string }) {
  return (
    <ContextMenu.Item className={destructive === true ? "b-menu-item b-destructive" : "b-menu-item"} onSelect={onSelect} data-testid={`menu-${testID}`}>
      {icon === undefined ? <span className="b-menu-icon-space" /> : <Icon name={icon} size="icon.sm" />}
      <span>{label}</span>
    </ContextMenu.Item>
  );
}

function Separator() {
  return <ContextMenu.Separator className="tab-menu-sep" />;
}

/** The split-tab side (tab id) an event happened on. */
function segmentOf(target: EventTarget): string | undefined {
  return target instanceof Element ? (target.closest<HTMLElement>("[data-seg]")?.dataset.seg ?? undefined) : undefined;
}

function UnitTab({ item, s }: { item: Extract<StripItem, { kind: "unit" }>; s: StripProps }) {
  const t = useT();
  const { shell, state } = s.p;
  const u = item.unit;
  const { ref, dragging, drop } = useStripItemDnd(item);
  const { source, intent } = useTabDrag();
  // One side of this split tab being dragged, and the side it would swap with.
  const segDrag = source?.kind === "unit" && source.key === item.key ? source.seg : undefined;
  const swapWith = intent?.kind === "swap" && intent.key === item.key ? intent.b : undefined;
  const [menuSeg, setMenuSeg] = useState<string | undefined>(undefined);
  // A group created from this menu opens its editor once the menu has closed (and kept focus from the tab).
  const editAfterClose = useRef<string | undefined>(undefined);
  const isSelected = u.tabIds.some((id) => s.selected.includes(id));
  const titles = u.tabIds.map((id) => s.info(id).title);

  // The tabs a menu acts on: the selection when this entry is part of it, else this entry.
  const targets = isSelected && s.selected.length > u.tabIds.length ? s.selected : u.tabIds;
  const multi = targets.length > u.tabIds.length;
  const anchor = u.tabIds[0]!;

  const select = (tabId: string, e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      // ⌘/Ctrl-click: (de)select the whole entry.
      s.setSelected((cur) => (u.tabIds.some((id) => cur.includes(id)) ? cur.filter((id) => !u.tabIds.includes(id)) : [...cur, ...u.tabIds]));
      return;
    }
    s.setSelected((cur) => (cur.length === 0 ? cur : []));
    shell.activateTab(tabId);
  };
  const close = () => (u.split ? shell.closeSplit(anchor) : shell.closeTab(anchor));
  const groups = state.groups.filter((g) => !targets.every((id) => g.tabIds.includes(id)));
  const grouped = targets.some((id) => state.groups.some((g) => g.tabIds.includes(id)));
  const pinned = u.pinned;
  // The side of a split tab the menu acts on: the one right-clicked, else the focused one.
  const seg = menuSeg !== undefined && u.tabIds.includes(menuSeg) ? menuSeg : u.tabIds.includes(state.activeTabId) ? state.activeTabId : anchor;
  const url = Shell.findTab(state, seg)?.url ?? "";
  const lone = (id: string) => Shell.splitMembers(state, id).length === 1;
  const canSplitSelection = multi && state.maxPanes > 1 && targets.every(lone);

  return (
    <ContextMenu.Root
      onOpenChange={(open) => {
        // Right-clicking outside the selection acts on that entry alone (Chrome).
        if (open && !isSelected) s.setSelected((cur) => (cur.length === 0 ? cur : []));
      }}
    >
      <ContextMenu.Trigger asChild>
        <div
          ref={ref}
          role="tab"
          aria-selected={u.shown}
          // Present (empty) so dnd-kit's accessibility plugin leaves them alone: it would announce every tab as
          // "draggable" with keyboard drag instructions (there is no keyboard drag; an empty roledescription is ignored).
          aria-roledescription=""
          aria-describedby=""
          tabIndex={u.shown ? 0 : -1}
          title={titles.join(" | ")}
          data-testid={`tab-${u.id}`}
          data-unit={u.id}
          data-strip-key={item.key}
          data-group-color={item.group?.color}
          className={[
            "tab",
            u.split && "tab-split",
            u.shown && "tab-active",
            pinned && "tab-pinned",
            isSelected && "tab-selected",
            dragging && segDrag === undefined && "tab-dragging",
            drop !== undefined && `tab-drop-${drop}`,
            item.group !== undefined && "tab-in-group",
            item.lastInGroup && "tab-group-last",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={(e) => {
            if (!u.split) select(anchor, e);
          }}
          onAuxClick={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              // Middle click on one side of a split tab closes that side.
              const side = u.split ? segmentOf(e.target) : undefined;
              if (side !== undefined) shell.closeSplitSide(side);
              else close();
            }
          }}
          onContextMenu={(e) => setMenuSeg(u.split ? segmentOf(e.target) : undefined)}
          onKeyDown={(e) => {
            if (moveByKey(e, s, item)) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              shell.activateTab(anchor);
            }
          }}
        >
          {u.tabIds.map((id, i) => {
            const inf = s.info(id);
            return (
              <Fragment key={id}>
                {i > 0 ? <span className="tab-split-divider" aria-hidden /> : null}
                <span
                  className={[
                    "tab-seg",
                    u.split && u.shown && id === state.activeTabId && "tab-seg-active",
                    id === segDrag && "tab-seg-dragging",
                    id === swapWith && "tab-seg-swap",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-testid={u.split ? `tab-${id}` : undefined}
                  data-seg={u.split ? id : undefined}
                  onClick={
                    u.split
                      ? (e) => {
                          e.stopPropagation();
                          select(id, e);
                        }
                      : undefined
                  }
                >
                  <TabIcon info={inf} />
                  {pinned ? null : <span className="tab-title">{inf.title}</span>}
                  {u.split && !pinned ? (
                    // Each side of a split tab closes on its own (shown on hover and on the focused side); its partner
                    // stays as a normal tab. Not a Tab stop: the context menu has "Close …" for keyboard users.
                    <button
                      type="button"
                      className="tab-close tab-seg-close"
                      tabIndex={-1}
                      aria-hidden
                      aria-label={t("shell.split.closeSide", { title: inf.title })}
                      title={t("shell.split.closeSide", { title: inf.title })}
                      data-testid={`tab-seg-close-${id}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        shell.closeSplitSide(id);
                      }}
                    >
                      <Icon name="close" size="icon.sm" />
                    </button>
                  ) : null}
                </span>
              </Fragment>
            );
          })}
          {pinned || u.split ? null : (
            <button
              type="button"
              className="tab-close"
              // Not a Tab stop of its own (⌘W, middle click or the context menu close a focused tab), and kept out of
              // the tab's accessible name.
              tabIndex={-1}
              aria-hidden
              aria-label={t("shell.closeTab")}
              data-testid={`tab-close-${u.id}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                close();
              }}
            >
              <Icon name="close" size="icon.sm" />
            </button>
          )}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content
          className="b-menu"
          data-testid="tab-menu"
          onCloseAutoFocus={(e) => {
            const id = editAfterClose.current;
            if (id === undefined) return;
            e.preventDefault();
            editAfterClose.current = undefined;
            s.setEditing(id);
          }}
        >
          {u.split && !multi ? (
            <>
              <MenuItem icon="external" label={t("shell.split.moveToNewTab")} onSelect={() => shell.moveToNewTab(seg)} testID="split-move-to-new-tab" />
              <MenuItem icon="sort" label={t("shell.split.swap")} onSelect={() => shell.swapSplit(seg)} testID="split-swap" />
              <MenuItem icon="tabs" label={t("shell.split.separate")} onSelect={() => shell.separateSplit(anchor)} testID="split-separate" />
              <Separator />
            </>
          ) : null}
          {canSplitSelection ? <MenuItem icon="splitH" label={t("shell.splitRight")} onSelect={() => shell.splitWith(targets[0]!, targets.slice(1))} testID="tab-split-selection" /> : null}
          <MenuItem
            icon="group"
            label={t("shell.tab.addToNewGroup")}
            onSelect={() => {
              // Name it right away (Chrome opens its editor).
              editAfterClose.current = shell.createGroup(targets);
              s.setSelected(() => []);
            }}
            testID="tab-new-group"
          />
          {groups.length === 0 ? null : (
            <ContextMenu.Sub>
              <ContextMenu.SubTrigger className="b-menu-item" data-testid="menu-tab-add-to-group">
                <Icon name="folder" size="icon.sm" />
                <span className="tab-menu-grow">{t("shell.tab.addToGroup")}</span>
                <Icon name="chevronRight" size="icon.sm" />
              </ContextMenu.SubTrigger>
              <ContextMenu.Portal>
                <ContextMenu.SubContent className="b-menu" sideOffset={4}>
                  {groups.map((g) => (
                    <ContextMenu.Item key={g.id} className="b-menu-item" onSelect={() => shell.addToGroup(targets, g.id)} data-testid={`menu-add-to-${g.id}`}>
                      <span className="tab-group-dot" data-group-color={g.color} />
                      <span>{groupLabel(g, t)}</span>
                    </ContextMenu.Item>
                  ))}
                </ContextMenu.SubContent>
              </ContextMenu.Portal>
            </ContextMenu.Sub>
          )}
          {grouped ? <MenuItem icon="close" label={t("shell.tab.removeFromGroup")} onSelect={() => shell.removeFromGroup(targets)} testID="tab-ungroup" /> : null}
          <Separator />
          {multi ? null : <MenuItem icon="pin" label={pinned ? t("shell.unpinTab") : t("shell.pinTab")} onSelect={() => shell.pin(anchor, !pinned)} testID="tab-pin" />}
          {multi || u.split ? null : <MenuItem icon="copy" label={t("shell.tab.duplicate")} onSelect={() => shell.duplicate(anchor)} testID="tab-duplicate" />}
          {multi ? null : <MenuItem icon="link" label={t("shell.tab.copyLink")} onSelect={() => void navigator.clipboard.writeText(`${WEB_ORIGIN}${url}`)} testID="tab-copy-link" />}
          {multi || u.split || !isDesktop() ? null : (
            <MenuItem
              icon="window"
              label={t("shell.tab.moveToWindow")}
              onSelect={() =>
                void openInNewWindow(url).then((ok) => {
                  if (ok) shell.closeTabs([anchor]);
                })
              }
              testID="tab-new-window"
            />
          )}
          <Separator />
          {u.split && !multi ? (
            <>
              <MenuItem icon="close" label={t("shell.split.closeSide", { title: s.info(seg).title })} onSelect={() => shell.closeSplitSide(seg)} testID="split-close-side" />
              <MenuItem icon="close" label={t("shell.split.close")} onSelect={() => shell.closeSplit(anchor)} testID="split-close" />
            </>
          ) : (
            <MenuItem
              icon="close"
              label={multi ? t("shell.tab.closeSelected", { count: targets.length }) : t("shell.closeTab")}
              onSelect={() => {
                shell.closeTabs(targets);
                s.setSelected(() => []);
              }}
              testID="tab-close"
            />
          )}
          <MenuItem icon="close" label={t("shell.tab.closeOthers")} onSelect={() => shell.closeOthers(targets)} testID="tab-close-others" />
          <MenuItem icon="close" label={t("shell.tab.closeRight")} onSelect={() => shell.closeToTheRight(targets[targets.length - 1]!)} testID="tab-close-right" />
          {state.closed.length === 0 ? null : <MenuItem icon="reload" label={t("shell.tab.reopenClosed")} onSelect={() => shell.reopenClosed()} testID="tab-reopen" />}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

function GroupChip({ item, s }: { item: Extract<StripItem, { kind: "group" }>; s: StripProps }) {
  const t = useT();
  const { shell } = s.p;
  const g = item.group;
  const { ref, dragging, drop } = useStripItemDnd(item);
  const editing = s.editing === g.id;
  const editAfterClose = useRef(false);
  const label = groupLabel(g, t);
  return (
    <Popover.Root open={editing} onOpenChange={(o) => s.setEditing(o ? g.id : undefined)}>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          <Popover.Anchor asChild>
            <div
              ref={ref}
              // A tab in the tablist (a button there is invalid), expanded = its tabs are shown. role="tab" also keeps
              // dnd-kit from adding aria-pressed (it only does that for buttons); the empty roledescription / describedby
              // keep its "draggable" announcement off, as on the tabs.
              role="tab"
              aria-selected={false}
              aria-roledescription=""
              aria-describedby=""
              tabIndex={-1}
              aria-expanded={!g.collapsed}
              aria-label={`${label} · ${t("shell.tabs.count", { count: item.count })}`}
              title={`${label} · ${t("shell.tabs.count", { count: item.count })}`}
              data-testid={`tab-group-${g.id}`}
              data-strip-key={item.key}
              data-group-color={g.color}
              className={[
                "tab-group-chip",
                g.name === "" && "tab-group-chip-unnamed",
                g.collapsed && "tab-group-collapsed",
                dragging && "tab-dragging",
                drop !== undefined && `tab-drop-${drop}`,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => shell.setGroupCollapsed(g.id, !g.collapsed)}
              onDoubleClick={() => s.setEditing(g.id)}
              onKeyDown={(e) => {
                if (moveByKey(e, s, item)) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  shell.setGroupCollapsed(g.id, !g.collapsed);
                }
              }}
            >
              {g.name === "" ? null : <span className="tab-group-name">{g.name}</span>}
              {g.collapsed ? <span className="tab-group-count">{item.count}</span> : null}
            </div>
          </Popover.Anchor>
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content
            className="b-menu"
            data-testid="tab-group-menu"
            onCloseAutoFocus={(e) => {
              if (!editAfterClose.current) return;
              e.preventDefault();
              editAfterClose.current = false;
              s.setEditing(g.id);
            }}
          >
            <MenuItem icon="edit" label={t("shell.group.edit")} onSelect={() => (editAfterClose.current = true)} testID="group-edit" />
            <MenuItem icon="plus" label={t("shell.group.newTab")} onSelect={() => newTabInGroup(shell, g)} testID="group-new-tab" />
            <MenuItem
              icon={g.collapsed ? "chevronDown" : "chevronRight"}
              label={g.collapsed ? t("shell.group.expand") : t("shell.group.collapse")}
              onSelect={() => shell.setGroupCollapsed(g.id, !g.collapsed)}
              testID="group-collapse"
            />
            <Separator />
            <MenuItem icon="tabs" label={t("shell.group.ungroup")} onSelect={() => shell.ungroup(g.id)} testID="group-ungroup" />
            <MenuItem icon="close" label={t("shell.group.close")} onSelect={() => shell.closeGroup(g.id)} destructive testID="group-close" />
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>
      <Popover.Portal>
        <Popover.Content className="b-popover tab-group-editor" side="bottom" align="start" sideOffset={8} collisionPadding={12} data-testid="tab-group-editor">
          <GroupEditor group={g} shell={shell} onDone={() => s.setEditing(undefined)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A new start-page tab at the end of the group. */
function newTabInGroup(shell: ShellViewModel, g: TabGroup) {
  const last = g.tabIds[g.tabIds.length - 1];
  if (last === undefined) return;
  shell.activateTab(last);
  shell.newTab();
}

function GroupEditor({ group, shell, onDone }: { group: TabGroup; shell: ShellViewModel; onDone: () => void }) {
  const t = useT();
  const [name, setName] = useState(group.name);
  return (
    <div className="tab-group-editor-body">
      <TextField
        value={name}
        onChange={(v) => {
          setName(v);
          shell.renameGroup(group.id, v);
        }}
        placeholder={t("shell.group.namePlaceholder")}
        autoFocus
        onSubmit={onDone}
        onCancel={onDone}
        testID="tab-group-name"
      />
      <div className="tab-group-swatches" role="radiogroup">
        {Shell.GROUP_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={group.color === c}
            aria-label={t(COLOR_KEYS[c])}
            title={t(COLOR_KEYS[c])}
            className="tab-group-swatch"
            data-group-color={c}
            data-testid={`tab-group-color-${c}`}
            onClick={() => shell.setGroupColor(group.id, c)}
          >
            {group.color === c ? <Icon name="check" size="icon.sm" /> : null}
          </button>
        ))}
      </div>
      <div className="tab-group-editor-actions">
        <button type="button" className="b-menu-item" onClick={() => (newTabInGroup(shell, group), onDone())} data-testid="tab-group-editor-new-tab">
          <Icon name="plus" size="icon.sm" />
          <span>{t("shell.group.newTab")}</span>
        </button>
        <button type="button" className="b-menu-item" onClick={() => (shell.ungroup(group.id), onDone())} data-testid="tab-group-editor-ungroup">
          <Icon name="tabs" size="icon.sm" />
          <span>{t("shell.group.ungroup")}</span>
        </button>
        <button type="button" className="b-menu-item b-destructive" onClick={() => (shell.closeGroup(group.id), onDone())} data-testid="tab-group-editor-close">
          <Icon name="close" size="icon.sm" />
          <span>{t("shell.group.close")}</span>
        </button>
      </div>
    </div>
  );
}
